import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Shield, CheckCircle2, Clock, AlertTriangle, ChevronRight, ChevronLeft, Save, Upload } from "lucide-react";

const STEPS = [
  { key: "business", label: "Business" },
  { key: "address",  label: "Address" },
  { key: "id",       label: "ID Document" },
  { key: "bank",     label: "Bank" },
  { key: "review",   label: "Review" },
];

const BUSINESS_TYPES = [
  ["individual",  "Individual / Sole proprietor"],
  ["company",     "Company"],
  ["llc",         "LLC"],
  ["partnership", "Partnership"],
  ["other",       "Other"],
];

const ID_TYPES = [
  ["passport",       "Passport"],
  ["driver_license", "Driver's license"],
  ["national_id",    "National ID"],
  ["other",          "Other"],
];

const empty = {
  business_name: "", business_type: "individual", tax_id: "",
  address_line1: "", address_line2: "", city: "", region: "",
  postal_code: "", country: "",
  id_document_url: "", id_document_type: "passport",
  bank_account_holder: "", bank_account_last4: "", bank_routing: "",
};

function StatusBanner({ status, note }) {
  if (status === "approved") return (
    <div className="card p-4 border border-[#D4FF00]/40 bg-[#D4FF00]/5 flex items-center gap-3" data-testid="kyc-status-approved">
      <CheckCircle2 className="text-[#D4FF00]"/>
      <div>
        <div className="font-bold uppercase tracking-wider text-sm text-[#D4FF00]">Verified</div>
        <div className="text-xs text-[#A1A1AA]">Your seller verification is complete. You're cleared to sell across the platform.</div>
      </div>
    </div>
  );
  if (status === "pending") return (
    <div className="card p-4 border border-[#00F0FF]/40 bg-[#00F0FF]/5 flex items-center gap-3" data-testid="kyc-status-pending">
      <Clock className="text-[#00F0FF]"/>
      <div>
        <div className="font-bold uppercase tracking-wider text-sm text-[#00F0FF]">Under review</div>
        <div className="text-xs text-[#A1A1AA]">We'll notify you in your inbox once a decision is made (usually within 24-48h).</div>
      </div>
    </div>
  );
  if (status === "rejected") return (
    <div className="card p-4 border border-[#FF007F]/40 bg-[#FF007F]/5 flex items-start gap-3" data-testid="kyc-status-rejected">
      <AlertTriangle className="text-[#FF007F] mt-1"/>
      <div className="flex-1">
        <div className="font-bold uppercase tracking-wider text-sm text-[#FF007F]">Rejected</div>
        {note && <div className="text-xs text-[#FAFAFA] mt-1">Reviewer note: <span className="text-[#A1A1AA]">{note}</span></div>}
        <div className="text-xs text-[#A1A1AA] mt-2">Please correct the issues below and resubmit.</div>
      </div>
    </div>
  );
  return null;
}

export default function SellerKycWizard() {
  const [rec, setRec] = useState(null);
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [editMode, setEditMode] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/seller/kyc");
      setRec(data);
      if (data && data.status && data.status !== "not_submitted") {
        setForm({ ...empty, ...data });
      }
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const status = rec?.status || "not_submitted";
  const showWizard = useMemo(() => {
    if (editMode) return true;
    return status === "not_submitted" || status === "rejected";
  }, [status, editMode]);

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: v }));

  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 2_500_000) { setMsg("File too large (max 2.5MB)."); return; }
    const reader = new FileReader();
    reader.onload = () => set("id_document_url")(reader.result);
    reader.readAsDataURL(f);
  };

  const validateStep = () => {
    if (step === 0) return form.business_name.trim() && form.business_type;
    if (step === 1) return form.address_line1.trim() && form.city.trim()
      && form.postal_code.trim() && form.country.trim();
    if (step === 2) return !!form.id_document_url && !!form.id_document_type;
    if (step === 3) return form.bank_account_holder.trim()
      && (form.bank_account_last4.trim().length >= 2);
    return true;
  };

  const submit = async () => {
    setBusy(true); setMsg("");
    try {
      await api.post("/seller/kyc", form);
      setMsg("Submitted! We'll review within 24-48h.");
      setEditMode(false);
      await load();
      setStep(0);
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail) || "Failed to submit.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="seller-kyc-tab">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <div className="font-display font-black text-2xl uppercase">Seller verification</div>
          <div className="text-xs text-[#A1A1AA]">Complete KYC to unlock payouts &amp; advanced tools.</div>
        </div>
        {(status === "approved" || status === "pending") && !editMode && (
          <button onClick={() => { setEditMode(true); setStep(0); }} data-testid="kyc-edit-btn"
            className="btn-secondary text-xs">Update info</button>
        )}
      </div>
      <StatusBanner status={status} note={rec?.review_note}/>

      {!showWizard && (
        <div className="card p-5 space-y-3" data-testid="kyc-summary">
          <Row label="Business" value={`${rec?.business_name || "—"} (${rec?.business_type || "—"})`}/>
          <Row label="Tax ID"   value={rec?.tax_id || "—"}/>
          <Row label="Address"  value={[rec?.address_line1, rec?.address_line2, rec?.city, rec?.region, rec?.postal_code, rec?.country].filter(Boolean).join(", ")}/>
          <Row label="ID type"  value={rec?.id_document_type || "—"}/>
          <Row label="Bank"     value={`${rec?.bank_account_holder || "—"} • ••${rec?.bank_account_last4 || ""}`}/>
        </div>
      )}

      {showWizard && (
        <div className="card p-5">
          {/* Stepper */}
          <ol className="flex items-center justify-between mb-6 gap-2 overflow-x-auto" data-testid="kyc-stepper">
            {STEPS.map((s, i) => (
              <li key={s.key} className={`flex-1 min-w-[80px] text-center pb-2 border-b-2 ${i === step ? "border-[#D4FF00] text-[#D4FF00]" : i < step ? "border-[#52525B] text-[#A1A1AA]" : "border-[#3D2845] text-[#52525B]"}`}>
                <div className="text-[10px] uppercase tracking-widest font-bold">Step {i + 1}</div>
                <div className="text-sm">{s.label}</div>
              </li>
            ))}
          </ol>

          {/* Step bodies */}
          {step === 0 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="kyc-step-business">
              <Field label="Legal business name *" value={form.business_name} onChange={set("business_name")} testid="kyc-business-name"/>
              <Select label="Business type *" value={form.business_type} onChange={set("business_type")} options={BUSINESS_TYPES} testid="kyc-business-type"/>
              <Field label="Tax ID / VAT (optional)" value={form.tax_id} onChange={set("tax_id")} testid="kyc-tax-id"/>
            </div>
          )}
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="kyc-step-address">
              <Field label="Street address *" value={form.address_line1} onChange={set("address_line1")} testid="kyc-addr1"/>
              <Field label="Apt / Suite (optional)" value={form.address_line2} onChange={set("address_line2")} testid="kyc-addr2"/>
              <Field label="City *" value={form.city} onChange={set("city")} testid="kyc-city"/>
              <Field label="Region / State" value={form.region} onChange={set("region")} testid="kyc-region"/>
              <Field label="Postal code *" value={form.postal_code} onChange={set("postal_code")} testid="kyc-postal"/>
              <Field label="Country (ISO code, e.g. US) *" value={form.country} onChange={(v) => set("country")(v.toUpperCase().slice(0, 2))} testid="kyc-country"/>
            </div>
          )}
          {step === 2 && (
            <div className="space-y-3" data-testid="kyc-step-id">
              <Select label="Document type *" value={form.id_document_type} onChange={set("id_document_type")} options={ID_TYPES} testid="kyc-id-type"/>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">Upload document *</label>
                <label className="card p-6 flex items-center gap-3 cursor-pointer hover:border-[#D4FF00] border border-dashed border-[#3F3F46]" data-testid="kyc-id-drop">
                  <Upload size={20} className="text-[#D4FF00]"/>
                  <div className="flex-1">
                    <div className="text-sm font-bold">{form.id_document_url ? "File loaded ✓" : "Tap to choose a JPG / PNG / PDF (max 2.5MB)"}</div>
                    {form.id_document_url && <div className="text-[10px] text-[#A1A1AA] truncate">{form.id_document_url.slice(0, 80)}…</div>}
                  </div>
                  <input type="file" accept="image/*,application/pdf" className="hidden" onChange={onFile} data-testid="kyc-id-file"/>
                </label>
                {form.id_document_url?.startsWith("data:image") && (
                  <img src={form.id_document_url} alt="" className="mt-3 max-h-48 rounded border border-[#3D2845]"/>
                )}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="kyc-step-bank">
              <Field label="Account holder name *" value={form.bank_account_holder} onChange={set("bank_account_holder")} testid="kyc-bank-name"/>
              <Field label="Last 4 digits of account *" value={form.bank_account_last4} onChange={(v) => set("bank_account_last4")(v.replace(/\D/g, "").slice(0, 4))} testid="kyc-bank-last4"/>
              <Field label="Routing / IFSC / IBAN (optional)" value={form.bank_routing} onChange={set("bank_routing")} testid="kyc-bank-routing"/>
            </div>
          )}
          {step === 4 && (
            <div className="space-y-2" data-testid="kyc-step-review">
              <div className="text-sm text-[#A1A1AA] mb-2">Please review before submitting.</div>
              <Row label="Business" value={`${form.business_name} (${form.business_type})`}/>
              <Row label="Tax ID" value={form.tax_id || "—"}/>
              <Row label="Address" value={[form.address_line1, form.address_line2, form.city, form.region, form.postal_code, form.country].filter(Boolean).join(", ")}/>
              <Row label="ID type" value={form.id_document_type}/>
              <Row label="Bank" value={`${form.bank_account_holder} • ••${form.bank_account_last4}`}/>
            </div>
          )}

          {/* Footer */}
          {msg && <div className="text-sm mt-4 text-[#D4FF00]" data-testid="kyc-msg">{msg}</div>}
          <div className="flex justify-between mt-6">
            <button onClick={() => setStep(s => Math.max(0, s - 1))}
              disabled={step === 0}
              data-testid="kyc-prev"
              className="btn-secondary text-xs disabled:opacity-30"><ChevronLeft size={14} className="inline"/> Back</button>
            {step < STEPS.length - 1 ? (
              <button onClick={() => { if (validateStep()) setStep(s => s + 1); else setMsg("Please fill required fields."); }}
                data-testid="kyc-next"
                className="btn-primary text-xs">Next <ChevronRight size={14} className="inline"/></button>
            ) : (
              <button onClick={submit} disabled={busy || !validateStep()}
                data-testid="kyc-submit"
                className="btn-primary text-xs disabled:opacity-50"><Save size={14} className="inline mr-1"/>{busy ? "Submitting…" : "Submit for review"}</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex flex-col sm:flex-row sm:gap-3">
      <div className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold sm:w-32">{label}</div>
      <div className="text-sm flex-1 break-words">{value}</div>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">{label}</label>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="input-dark w-full px-3 py-2 text-sm"/>
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-[#A1A1AA] font-bold block mb-1">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="input-dark w-full px-3 py-2 text-sm">
        {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
      </select>
    </div>
  );
}
